import { useCan, useErrorMessage, formatTime, toast } from '@sentinel/web-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@sentinel/ui';
import { useTranslation } from 'react-i18next';
import { endpoints } from '@/core/api/endpoints';
import { useActiveMission } from '@/core/mission/active-mission';
import { keys } from '@/core/query/query-keys';
import { alarmText } from '@/modules/dashboard/chronicle-text';

/** Only an open level-4 alarm interrupts the interface: a band under the topbar with
 *  `aria-live="assertive"` and a nominal acknowledgement (rule 15). */
export function AlarmBar() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const { missionId } = useActiveMission();
  const canAcknowledge = useCan('alarm.acknowledge_l4');
  const alarms = useQuery({
    queryKey: keys.inventory(missionId ?? 'none', 'alarms'),
    queryFn: () => endpoints.missions.alarms(missionId as string),
    enabled: missionId !== null,
    refetchInterval: 30_000,
  });
  const acknowledge = useMutation({
    mutationFn: (alarmId: string) =>
      endpoints.missions.acknowledgeAlarm(missionId as string, alarmId),
    onSuccess: (alarm) => {
      toast(
        'crit',
        t('dashboard.alarms.takenToast', { code: alarm.code }),
        t('dashboard.alarms.takenToastDetail'),
      );
      void queryClient.invalidateQueries({ queryKey: ['mission', missionId] });
    },
    onError: (error) => toast('att', errorMessage(error)),
  });

  const l4 = alarms.data?.items.find((a) => a.level === 4 && a.status !== 'closed');
  if (missionId === null || l4 === undefined) return null;

  return (
    <div className="l4" role="alert" aria-live="assertive">
      <Icon name="warning" size={16} />
      <span className="cd">{l4.code}</span>
      <span className="lv">{t('chronicle.level', { level: l4.level })}</span>
      <span className="tx">
        <b>{t(`chronicle.codes.${l4.code}`)}</b> — {alarmText(t, l4)}
      </span>
      <span className="tm">
        <Icon name="clock" size={14} />
        {l4.status === 'acknowledged'
          ? t('shell.alarmBar.takenBy', {
              by: l4.acknowledgedBy ?? '',
              time: formatTime(l4.acknowledgedAt),
            })
          : `${t('shell.alarmBar.received', { time: formatTime(l4.raisedAt, true) })} · ${t('shell.alarmBar.awaiting')}`}
      </span>
      <span className="ac">
        {l4.status === 'open' && canAcknowledge ? (
          <button
            type="button"
            className="btn sm dngr"
            onClick={() => acknowledge.mutate(l4.id)}
            disabled={acknowledge.isPending}
          >
            {t('shell.alarmBar.take')}
          </button>
        ) : (
          <span className="badge crit">
            <Icon name="check" size={12} />
            {t('shell.alarmBar.taken')}
          </span>
        )}
      </span>
    </div>
  );
}
